import "../dashboardPage/dashboard.page.css";
import "../portal.css";
import "./roster.page.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Avatar, DatePicker, Dropdown, Select, Skeleton } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { Navbar } from "@src/components/navbar";
import { DutyDrawer } from "@src/components/dutyDrawer/dutyDrawer";
import { DutyRequestFormModal } from "@src/components/dutyRequestForm/dutyRequestFormModal";
import { getCustomerPortalRosterQuery } from "@src/util/query/portal.query";
import { getAllSitesForCustomerQuery } from "@src/util/query/site.query";
import { downloadCsv, downloadServerPdf } from "@src/util/portal/download";
import { formatHours, groupRows, officerName, sourceLabel, stateMeta, timeRange } from "@src/util/portal/dutyState";

dayjs.extend(isoWeek);

// Schedule (shown to the customer as "Schedule"; called the roster in code) - every duty booked at the customer's sites for a day / week / month,
// all sites or one. Views: By site / By officer (a row each down the left, a
// column per day), Calendar (the schedule page's layout: days across, a band per
// site) and List. Duty cards are the schedule page's duty card. A duty opens the
// duty drawer (proof of attendance + the duty reports). View, period, date and
// site live in the URL so a refresh or a shared link lands on the same roster.

const VIEWS = [
    { key: "site", label: "By site" },
    { key: "officer", label: "By officer" },
    { key: "calendar", label: "Calendar" },
    { key: "list", label: "List" },
];
// current: the button that jumps back to now, worded for the period on screen
const PERIODS = [
    { key: "day", label: "Day", current: "Today" },
    { key: "week", label: "Week", current: "This week" },
    { key: "month", label: "Month", current: "This month" },
];

// the window fetched for a period (month = the whole calendar grid around it)
export const rosterRange = (period, date) => {
    const d = dayjs(date);
    if (period === "day") return [d.startOf("day"), d.endOf("day")];
    if (period === "week") return [d.startOf("isoWeek"), d.endOf("isoWeek")];
    return [d.startOf("month").startOf("isoWeek"), d.endOf("month").endOf("isoWeek")];
};

// the schedule page's status badge (schedule.css openDuty / confirmDuty / ...),
// in the customer's words
const CARD_STATUS = {
    completed:     { label: "Completed", cls: "completedDuty" },
    onDuty:        { label: "On Duty",   cls: "confirmDuty" },
    upcoming:      { label: "Upcoming",  cls: "confirmDuty" },
    awaiting:      { label: "Due",       cls: "confirmDuty" },
    missedClockIn: { label: "Late",      cls: "unConfirmDuty" },
    noShow:        { label: "Pending",   cls: "noShowDuty" },
    unassigned:    { label: "Open",      cls: "openDuty" },
};

const initials = (name) => {
    const words = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "";
    return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase();
};

const dutyLine = (row) => [row.hourType, row.dutyCode || row.dutyType || sourceLabel(row)].filter(Boolean).join(", ");

// The schedule page's duty card: avatar | time + status / hour type, duty code /
// the officer (or the site, on an officer's row)
const DutyCard = ({ row, onOpen, show = "officer" }) => {
    const status = CARD_STATUS[row.state] || CARD_STATUS.upcoming;
    const open = row.state === "unassigned";
    const name = officerName(row);
    return (
        <div className="cp-duty-card" role="button" tabIndex={0} onClick={() => onOpen(row)} onKeyDown={(e) => e.key === "Enter" && onOpen(row)}
            title={`${timeRange(row.start, row.end)} · ${row.siteName} · ${name} · ${stateMeta(row.state).label}`}>
            <span className="cp-duty-avatar">
                <Avatar size={40} className={open ? "cp-avatar-open" : ""}>{open ? <UserAddOutlined /> : initials(name)}</Avatar>
            </span>
            <div className="cp-duty-main">
                <div className="duty-time">
                    <div className="schedule-time">{dayjs(row.start).format("HH:mm")}-{row.end ? dayjs(row.end).format("HH:mm") : ""}</div>
                    <div className={`${status.cls} schedule-status`}>{status.label}</div>
                </div>
                <div className="schedule-dutyType">{dutyLine(row)}</div>
                <div className="schedule-name">{show === "site" ? row.siteName : name}</div>
            </div>
        </div>
    );
};

// month cells are narrow: start time only, coloured like the status badge
const MiniCard = ({ row, onOpen, withSite }) => {
    const status = CARD_STATUS[row.state] || CARD_STATUS.upcoming;
    return (
        <button type="button" className="cp-mini" onClick={() => onOpen(row)} title={`${timeRange(row.start, row.end)} · ${row.siteName} · ${officerName(row)} · ${stateMeta(row.state).label}`}>
            <span className={`cp-mini-bar ${status.cls}`} />
            <b>{dayjs(row.start).format("HH:mm")}</b>
            {withSite && <span>{row.siteName}</span>}
        </button>
    );
};

const dayKey = (d) => dayjs(d).format("YYYY-MM-DD");
const splitByDay = (rows) => {
    const map = new Map();
    rows.forEach((r) => {
        const key = dayKey(r.start);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(r);
    });
    return map;
};

const DayHead = ({ d, todayKey, onDay, short }) => {
    const key = dayKey(d);
    return (
        <button type="button" className="rs-grid-day" onClick={() => onDay(key)}>
            {short ? <><span>{d.format("dd")}</span> <b>{d.format("D")}</b></> : <>{d.format("ddd, MMM DD")}{key === todayKey ? " (Today)" : ""}</>}
        </button>
    );
};

// By site / By officer: a row each down the left, a column per day
const ResourceGrid = ({ groups, days, by, month, todayKey, onOpen, onDay }) => {
    // a month is wider than the screen - open it scrolled to today
    const wrapRef = useRef(null);
    useEffect(() => {
        const wrap = wrapRef.current;
        const today = wrap?.querySelector("thead .rs-grid-today");
        const name = wrap?.querySelector("thead .rs-grid-name");
        if (wrap && today && name) wrap.scrollLeft = Math.max(0, today.offsetLeft - name.offsetWidth - 2 * today.offsetWidth);
    }, [days.length, groups.length]);

    return (
        <section className="cdb-card rs-grid-card">
            {groups.length === 0 ? <div className="cdb-empty">No duties booked in this period.</div> : (
                <div className="rs-grid-wrap" ref={wrapRef}>
                    <table className={`rs-grid${month ? " rs-grid-month" : ""}${days.length === 1 ? " rs-grid-single" : ""}`}>
                        <thead>
                            <tr>
                                <th className="rs-grid-name">{by === "site" ? "Site" : "Officer"}</th>
                                {days.map((d) => (
                                    <th key={dayKey(d)} className={dayKey(d) === todayKey ? "rs-grid-today" : ""}>
                                        <DayHead d={d} todayKey={todayKey} onDay={onDay} short={month} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((g) => {
                                const byDay = splitByDay(g.rows);
                                return (
                                    <tr key={g.key} className={g.unassigned ? "rs-grid-unassigned" : ""}>
                                        <th className="rs-grid-name" scope="row">
                                            <b>{g.label}</b>
                                            <span>{g.rows.length ? `${g.rows.length} dut${g.rows.length === 1 ? "y" : "ies"} · ${formatHours(g.hours)}` : "No duties"}</span>
                                        </th>
                                        {days.map((d) => (
                                            <td key={dayKey(d)} className={dayKey(d) === todayKey ? "rs-grid-today" : ""}>
                                                <div className="rs-td">
                                                    {(byDay.get(dayKey(d)) || []).map((r) => (month
                                                        ? <MiniCard key={r._id} row={r} onOpen={onOpen} />
                                                        : <DutyCard key={r._id} row={r} onOpen={onOpen} show={by === "site" ? "officer" : "site"} />))}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

// Calendar, day / week: the schedule page's layout - days across the top (today
// highlighted), a band per site, that site's duties under each day
const ScheduleCalendar = ({ groups, days, todayKey, onOpen, onDay }) => (
    <section className="cdb-card rs-grid-card">
        {groups.length === 0 ? <div className="cdb-empty">No duties booked in this period.</div> : (
            <div className="rs-grid-wrap">
                <table className={`cp-schedule${days.length === 1 ? " cp-schedule-day" : ""}`} cellSpacing={0}>
                    <thead>
                        <tr>
                            {days.map((d) => (
                                <th key={dayKey(d)} className={dayKey(d) === todayKey ? "cp-schedule-today" : ""}>
                                    <DayHead d={d} todayKey={todayKey} onDay={onDay} />
                                </th>
                            ))}
                        </tr>
                    </thead>
                    {groups.map((g) => {
                        const byDay = splitByDay(g.rows);
                        return (
                            <tbody key={g.key}>
                                <tr><td colSpan={days.length} className="cp-schedule-area"><div className="cp-schedule-area-label"><b>{g.label}</b><span>{g.rows.length} dut{g.rows.length === 1 ? "y" : "ies"} · {formatHours(g.hours)}</span></div></td></tr>
                                <tr>
                                    {days.map((d) => (
                                        <td key={dayKey(d)} className={dayKey(d) === todayKey ? "cp-schedule-today" : ""}>
                                            <div className="rs-td">
                                                {(byDay.get(dayKey(d)) || []).map((r) => <DutyCard key={r._id} row={r} onOpen={onOpen} show="officer" />)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        );
                    })}
                </table>
            </div>
        )}
    </section>
);

// Calendar, month: a month grid, a few duties a day
const MonthCalendar = ({ rows, gridDays, date, todayKey, onOpen, onDay }) => {
    const byDay = splitByDay(rows);
    return (
        <section className="rs-month">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="rs-month-dow">{d}</div>)}
            {gridDays.map((d) => {
                const key = dayKey(d);
                const list = byDay.get(key) || [];
                return (
                    <div key={key} className={`rs-cell${d.month() !== date.month() ? " rs-outside" : ""}${key === todayKey ? " rs-today" : ""}`}>
                        <button type="button" className="rs-cell-date" onClick={() => onDay(key)}>{d.format("D")}</button>
                        {list.slice(0, 3).map((r) => <MiniCard key={r._id} row={r} onOpen={onOpen} withSite />)}
                        {list.length > 3 && <button type="button" className="rs-more" onClick={() => onDay(key)}>+{list.length - 3} more</button>}
                    </div>
                );
            })}
        </section>
    );
};

export const Roster = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const view = VIEWS.some((v) => v.key === params.get("view")) ? params.get("view") : "site";
    const period = PERIODS.some((p) => p.key === params.get("period")) ? params.get("period") : "week";
    const date = dayjs(params.get("date") || undefined).isValid() ? dayjs(params.get("date") || undefined) : dayjs();
    const siteId = params.get("site") || null;

    const setParam = (changes) => {
        const next = new URLSearchParams(params);
        Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        setParams(next, { replace: true });
    };
    // a day's header opens that day
    const openDay = (key) => setParam({ period: "day", date: key });

    const [customerDetail, setCustomerDetail] = useState(null);
    const [openDuty, setOpenDuty] = useState(null);
    const [requestModalOpen, setRequestModalOpen] = useState(false);
    useEffect(() => {
        const detail = localStorage.getItem("customerDetail");
        if (detail) setCustomerDetail(JSON.parse(detail));
    }, []);

    const [rangeStart, rangeEnd] = rosterRange(period, date);
    const variables = useMemo(() => ({
        from: rangeStart.toISOString(),
        to: rangeEnd.toISOString(),
        siteId,
    }), [rangeStart.valueOf(), rangeEnd.valueOf(), siteId]); // eslint-disable-line react-hooks/exhaustive-deps

    const { data, loading, refetch } = useQuery(getCustomerPortalRosterQuery, { variables, fetchPolicy: "cache-and-network" });
    const { data: sitesData } = useQuery(getAllSitesForCustomerQuery, {
        variables: { customerId: customerDetail?._id },
        skip: !customerDetail?._id,
        fetchPolicy: "cache-first",
    });
    const sites = sitesData?.getAllSiteForCustomer?.response || [];

    const roster = data?.getCustomerPortalRoster?.response;
    const fetched = useMemo(() => roster?.rows || [], [roster]);
    // the month grid fetches the padding weeks around the month; everything else is the month itself
    const rows = useMemo(() => (period === "month" && view !== "calendar"
        ? fetched.filter((r) => dayjs(r.start).month() === date.month())
        : fetched), [fetched, period, view, date.month()]); // eslint-disable-line react-hooks/exhaustive-deps
    const isLoading = loading && !roster;

    // By site lists every one of the customer's sites (empty ones too, so a gap
    // in cover shows); By officer and Calendar list only who/where has duties
    const groups = useMemo(() => {
        if (view === "officer") return groupRows(rows, "officer");
        const withDuties = groupRows(rows, "site");
        if (view !== "site") return withDuties;
        const have = new Map(withDuties.map((g) => [g.key, g]));
        const all = (roster?.sites || []).map((s) => have.get(String(s._id)) || { key: String(s._id), label: s.sitename, rows: [], hours: 0 });
        withDuties.forEach((g) => { if (!all.some((a) => a.key === g.key)) all.push(g); });
        return all;
    }, [rows, view, roster]);

    const days = period === "day"
        ? [date.startOf("day")]
        : period === "week"
            ? Array.from({ length: 7 }, (_, i) => rangeStart.add(i, "day"))
            : Array.from({ length: date.daysInMonth() }, (_, i) => date.startOf("month").add(i, "day"));
    const gridDays = [];
    for (let d = rangeStart; !d.isAfter(rangeEnd, "day"); d = d.add(1, "day")) gridDays.push(d);
    const todayKey = dayjs().format("YYYY-MM-DD");

    const step = (dir) => setParam({ date: date.add(dir, period).format("YYYY-MM-DD") });
    const periodLabel = period === "day"
        ? date.format("dddd D MMMM YYYY")
        : period === "week"
            ? `${rangeStart.format("D MMM")} – ${rangeEnd.format("D MMM YYYY")}`
            : date.format("MMMM YYYY");

    const statRows = view === "calendar" && period === "month" ? rows.filter((r) => dayjs(r.start).month() === date.month()) : rows;
    const attention = statRows.filter((r) => stateMeta(r.state).attention).length;
    const unassigned = statRows.filter((r) => r.state === "unassigned").length;
    const bookedHours = statRows.reduce((sum, r) => sum + (r.hours || 0), 0);

    const exportCsv = () => {
        downloadCsv(
            `schedule-${rangeStart.format("YYYY-MM-DD")}-to-${rangeEnd.format("YYYY-MM-DD")}.csv`,
            ["Date", "Start", "End", "Site", "Service", "Hour type", "Duty type", "Officer", "Booked hours", "Clock in", "Clock out", "Hours on duty", "Status"],
            statRows.map((r) => [
                dayjs(r.start).format("DD/MM/YYYY"),
                dayjs(r.start).format("HH:mm"),
                r.end ? dayjs(r.end).format("HH:mm") : "",
                r.siteName,
                sourceLabel(r),
                r.hourType || "",
                r.dutyType,
                officerName(r),
                r.hours,
                r.clockIn ? dayjs(r.clockIn.at).format("HH:mm") : "",
                r.clockOut ? dayjs(r.clockOut.at).format("HH:mm") : "",
                r.actualHours ?? "",
                stateMeta(r.state).label,
            ])
        );
    };

    // the PDF is built by the server and saved straight to downloads (no print dialog)
    const [printFrom, printTo] = period === "month" ? [date.startOf("month"), date.endOf("month")] : [rangeStart, rangeEnd];
    const downloadPdf = () => downloadServerPdf("/reports/customer/schedule.pdf", {
        from: printFrom.toISOString(), to: printTo.toISOString(), siteId,
        by: view === "site" || view === "officer" ? view : null,
    }, `schedule-${printFrom.format("YYYY-MM-DD")}-to-${printTo.format("YYYY-MM-DD")}.pdf`);

    return (
        <div className="cdb-page">
            <Navbar customerDetail={customerDetail} />

            <div className="cdb-body">
                <header className="cp-page-head">
                    <div>
                        <h1 className="cdb-title">Schedule</h1>
                        <div className="cp-page-sub">Every duty booked at your sites. Tap a duty for proof of attendance and its reports.</div>
                    </div>
                    <div className="cdb-header-actions">
                        <Dropdown
                            trigger={["click"]}
                            menu={{
                                items: [
                                    { key: "pdf", label: "PDF", onClick: downloadPdf },
                                    { key: "csv", label: "Excel (CSV)", onClick: exportCsv, disabled: !statRows.length },
                                ],
                            }}
                        >
                            <button type="button" className="cdb-btn">Download ▾</button>
                        </Dropdown>
                        <button type="button" className="cdb-btn cdb-btn-primary" onClick={() => setRequestModalOpen(true)}>Request a duty</button>
                    </div>
                </header>

                <div className="cp-toolbar">
                    <div className="cdb-segmented" role="tablist" aria-label="Schedule view">
                        {VIEWS.map((v) => (
                            <button key={v.key} type="button" role="tab" aria-selected={view === v.key} className={view === v.key ? "active" : ""} onClick={() => setParam({ view: v.key })}>
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <div className="cdb-segmented" role="tablist" aria-label="Period">
                        {PERIODS.map((p) => (
                            <button key={p.key} type="button" role="tab" aria-selected={period === p.key} className={period === p.key ? "active" : ""} onClick={() => setParam({ period: p.key })}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="cp-period">
                        <button type="button" className="cp-icon-btn" onClick={() => step(-1)} aria-label="Previous">‹</button>
                        <button type="button" className="cdb-btn cp-today-btn" onClick={() => setParam({ date: null })}>{PERIODS.find((p) => p.key === period)?.current || "Today"}</button>
                        <button type="button" className="cp-icon-btn" onClick={() => step(1)} aria-label="Next">›</button>
                        {/* click the period to jump straight to any day / month / year */}
                        <span className="cp-period-jump">
                            <span className="cp-period-label">{periodLabel} <span className="cp-period-caret">▾</span></span>
                            <DatePicker
                                key={period}
                                picker={period === "month" ? "month" : "date"}
                                value={date}
                                allowClear={false}
                                inputReadOnly
                                className="cp-period-picker"
                                popupClassName="cp-period-pop"
                                onChange={(d) => d && setParam({ date: d.format("YYYY-MM-DD") })}
                            />
                        </span>
                    </div>
                    <Select
                        className="cp-site-select"
                        value={siteId || "all"}
                        onChange={(v) => setParam({ site: v === "all" ? null : v })}
                        showSearch
                        optionFilterProp="label"
                        options={[{ value: "all", label: "All sites" }, ...sites.map((s) => ({ value: s._id, label: s.sitename }))]}
                    />
                </div>

                <div className="cp-stats">
                    <div><b>{statRows.length}</b> dut{statRows.length === 1 ? "y" : "ies"}</div>
                    <div><b>{formatHours(bookedHours)}</b> booked</div>
                    <div className={unassigned ? "cp-stat-warn" : ""}><b>{unassigned}</b> need{unassigned === 1 ? "s" : ""} an officer</div>
                    <div className={attention - unassigned ? "cp-stat-alert" : ""}><b>{attention - unassigned}</b> missed</div>
                    <button type="button" className="cdb-link" onClick={() => refetch()}>Refresh</button>
                </div>

                {isLoading ? (
                    <section className="cdb-card"><div className="cdb-card-body"><Skeleton active paragraph={{ rows: 8 }} /></div></section>
                ) : view === "site" || view === "officer" ? (
                    <ResourceGrid groups={groups} days={days} by={view} month={period === "month"} todayKey={todayKey} onOpen={setOpenDuty} onDay={openDay} />
                ) : view === "calendar" ? (
                    period === "month"
                        ? <MonthCalendar rows={rows} gridDays={gridDays} date={date} todayKey={todayKey} onOpen={setOpenDuty} onDay={openDay} />
                        : <ScheduleCalendar groups={groups} days={days} todayKey={todayKey} onOpen={setOpenDuty} onDay={openDay} />
                ) : (
                    <section className="cdb-card">
                        {rows.length === 0 ? <div className="cdb-empty">No duties booked in {periodLabel}.</div> : (
                            <div className="cp-table-wrap">
                                <table className="cp-table">
                                    <thead>
                                        <tr><th>Date</th><th>Time</th><th>Site</th><th>Duty</th><th>Officer</th><th className="cp-num">Hours</th><th>Status</th></tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const m = stateMeta(r.state);
                                            return (
                                                <tr key={r._id} className="cp-row-click" onClick={() => setOpenDuty(r)}>
                                                    <td>{dayjs(r.start).format("ddd D MMM")}</td>
                                                    <td>{timeRange(r.start, r.end)}</td>
                                                    <td>{r.siteName}</td>
                                                    <td>{[r.hourType, r.dutyType || sourceLabel(r)].filter(Boolean).join(", ")}</td>
                                                    <td>{officerName(r)}</td>
                                                    <td className="cp-num">{formatHours(r.hours)}</td>
                                                    <td><span className="cdb-pill" style={{ color: m.color, borderColor: m.color, background: m.bg }}>{m.label}</span></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                )}

                <div className="cp-footnote">
                    Looking for older duty reports or the full activity log of a site?{" "}
                    <button type="button" className="cdb-link" onClick={() => navigate(siteId ? `/customer/reports/${siteId}` : "/customer/reports")}>Schedules & activity ›</button>
                </div>
            </div>

            <DutyDrawer duty={openDuty} onClose={() => setOpenDuty(null)} />

            {requestModalOpen && (
                <DutyRequestFormModal
                    visible={requestModalOpen}
                    onClose={() => setRequestModalOpen(false)}
                    sites={sites}
                    onSuccess={() => refetch()}
                />
            )}
        </div>
    );
};
